import { useEffect, useState } from "react";
import GoogleSearchConsolePanel from "./GoogleSearchConsolePanel";
import CloudflarePanel from "./CloudflarePanel";
import RedirectManager from "./RedirectManager";
import { api } from "./lib/api";
import type { Site } from "./types";

interface Audit {
  url: string;
  status: number;
  checkedAt: string;
  responseTimeMs: number;
  title: string | null;
  description: string | null;
  canonical: string | null;
  robots: string | null;
  h1Count: number;
  imageCount: number;
  missingAltCount: number;
  structuredDataCount: number;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImage: string | null;
  robotsStatus: number | null;
  sitemapStatus: number | null;
  cfCacheStatus: string | null;
  server: string | null;
}

function Check({
  label,
  value,
  good,
}: {
  label: string;
  value: string;
  good: boolean;
}) {
  return (
    <article className={good ? "live-good" : "live-warn"}>
      <span>{good ? "✓" : "!"}</span>
      <div>
        <strong>{label}</strong>
        <small>{value}</small>
      </div>
    </article>
  );
}

export default function LiveSeoPanel({
  site,
  admin,
}: {
  site: Site;
  admin: boolean;
}) {
  const [audit, setAudit] = useState<Audit | null>(null);
  const [busy, setBusy] = useState(Boolean(site.domain));
  const [error, setError] = useState("");
  async function run() {
    if (!site.domain) {
      setError("Collega prima il dominio pubblico del sito");
      setBusy(false);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await api<{ audit: Audit }>(
        `/api/sites/${site.id}/seo/live`,
      );
      setAudit(result.audit);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Analisi non riuscita");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    if (!site.domain) return;
    api<{ audit: Audit }>(`/api/sites/${site.id}/seo/live`)
      .then((result) => {
        setAudit(result.audit);
        setError("");
      })
      .catch((cause) =>
        setError(
          cause instanceof Error ? cause.message : "Analisi non riuscita",
        ),
      )
      .finally(() => setBusy(false));
  }, [site.domain, site.id]);
  return (
    <>
      <section className="live-seo-panel">
        <div className="live-seo-head">
          <div>
            <p className="eyebrow indigo">Sito pubblicato</p>
            <h3>Controllo SEO dal vivo</h3>
            <p>
              Legge i tag che il dominio restituisce realmente a browser e
              crawler.
            </p>
          </div>
          <button
            className="secondary"
            onClick={run}
            disabled={busy || !site.domain}
          >
            {busy ? "Analisi…" : "Aggiorna dati"}
          </button>
        </div>
        {(error || !site.domain) && (
          <div className="notice error">
            {error || "Collega prima il dominio pubblico del sito"}
          </div>
        )}
        {site.domain && audit && (
          <>
            <div className="live-serp">
              <div className="serp-browser">
                <span>Google</span>
                <small>Anteprima indicativa dai tag pubblicati</small>
              </div>
              <span className="serp-url">{audit.url}</span>
              <strong>
                {audit.title || "Titolo non presente nella pagina live"}
              </strong>
              <p>
                {audit.description ||
                  "La pagina live non contiene una meta description."}
              </p>
              <small>
                Google può riscrivere titolo e descrizione in base alla ricerca
                dell’utente.
              </small>
            </div>
            <div className="live-check-grid">
              <Check
                label="Risposta pagina"
                value={`HTTP ${audit.status} · ${audit.responseTimeMs} ms`}
                good={audit.status >= 200 && audit.status < 300}
              />
              <Check
                label="Titolo"
                value={
                  audit.title ? `${audit.title.length} caratteri` : "Mancante"
                }
                good={Boolean(audit.title && audit.title.length <= 70)}
              />
              <Check
                label="Meta description"
                value={
                  audit.description
                    ? `${audit.description.length} caratteri`
                    : "Mancante"
                }
                good={Boolean(
                  audit.description && audit.description.length <= 180,
                )}
              />
              <Check
                label="Canonical"
                value={audit.canonical || "Mancante"}
                good={Boolean(audit.canonical)}
              />
              <Check
                label="Titolo H1"
                value={`${audit.h1Count} ${audit.h1Count === 1 ? "trovato" : "trovati"}`}
                good={audit.h1Count === 1}
              />
              <Check
                label="ALT immagini"
                value={`${audit.missingAltCount} mancanti su ${audit.imageCount}`}
                good={audit.missingAltCount === 0}
              />
              <Check
                label="Dati strutturati"
                value={`${audit.structuredDataCount} markup JSON-LD`}
                good={audit.structuredDataCount > 0}
              />
              <Check
                label="Sitemap e robots"
                value={`sitemap ${audit.sitemapStatus || "n/d"} · robots ${audit.robotsStatus || "n/d"}`}
                good={audit.sitemapStatus === 200 && audit.robotsStatus === 200}
              />
            </div>
            <div className="live-sources">
              {admin && <div>
                <small>Cloudflare / rete</small>
                <strong>
                  {audit.cfCacheStatus
                    ? `Cache ${audit.cfCacheStatus}`
                    : audit.server ||
                      "Dati rete disponibili al collegamento API"}
                </strong>
              </div>}
              <div>
                <small>Ultimo controllo</small>
                <strong>
                  {new Intl.DateTimeFormat("it-IT", {
                    dateStyle: "short",
                    timeStyle: "short",
                  }).format(new Date(audit.checkedAt))}
                </strong>
              </div>
            </div>
          </>
        )}
      </section>
      <GoogleSearchConsolePanel
        siteId={site.id}
        domain={site.domain}
        admin={admin}
      />
      {admin && <CloudflarePanel siteId={site.id} />}
      {admin && site.editorMode === "builder" && (
        <RedirectManager siteId={site.id} />
      )}
    </>
  );
}
